/**
 * Review State Helpers
 *
 * Pure functions for managing review traveller state.
 *
 * Architecture:
 * - rawTemplateData: Immutable API response (FlightsReviewTravellersTemplateData)
 * - uiState: Mutable state for user interactions (ReviewTravellerUiState)
 *
 * This file provides:
 * 1. UI state initialization from raw template data
 * 2. Fare summary update helpers
 * 3. Saved traveller extraction helpers
 */
import type {
  ReviewPrice,
  FareBreakup,
  PaxType,
  SavedTravellersState,
  ContactDetailsFormValues,
  GstDetailsFormValues,
  ContactDetailsFormConfig,
  GstDetailsFormConfig,
  FlightsReviewTravellersTemplateData,
  FlightsReviewInterstitialTemplateData,
} from '@contracts';
import type { FareSummary, SelectedFareState, SelectedFareCombo } from '../types/flightsTypes';
import { extractSelectedFaresWithCombo } from '@/features/flights/utils/reviewUtils';
import type { ReviewTravellerUiState } from '../slices/templateReviewTravellerSlice';

// ============================================
// Default Values
// ============================================

/** Default empty contact details */
export const EMPTY_CONTACT_DETAILS: ContactDetailsFormValues = {
  COUNTRY_CODE: '',
  COUNTRY_NAME: '',
  EMAIL: '',
  MOBILE_NUMBER: '',
};

/** Default empty GST details */
export const EMPTY_GST_DETAILS: GstDetailsFormValues = {
  REGISTERED_COMPANY_NAME: '',
  REGISTRATION_NO: '',
  optIn: false,
};

// ============================================
// Fare Summary Helpers
// ============================================

/**
 * Update fare summary from addon/meal/baggage/coupon selection response
 * This logic is repeated 4 times in reviewSlice - now centralized here
 */
export function updateFareSummaryFromResponse(
  currentFareSummary: FareSummary,
  responseFareSummary: {
    fareBreakup?: unknown;
    reviewPrice?: unknown;
  },
): FareSummary {
  const newFareBreakup = (responseFareSummary.fareBreakup ??
    currentFareSummary.fareBreakup) as FareBreakup | null;

  let updatedReviewPrice = (responseFareSummary.reviewPrice ??
    currentFareSummary.reviewPrice) as ReviewPrice | null;

  // If no reviewPrice in response but we have a new grandTotal, update the price
  if (
    !responseFareSummary.reviewPrice &&
    newFareBreakup?.grandTotal &&
    currentFareSummary.reviewPrice
  ) {
    updatedReviewPrice = {
      ...currentFareSummary.reviewPrice,
      price: newFareBreakup.grandTotal,
    };
  }

  return {
    reviewPrice: updatedReviewPrice,
    fareBreakup: newFareBreakup,
  };
}

// ============================================
// UI State Initialization
// ============================================

/**
 * Initialize UI state from raw template data
 *
 * Called when:
 * 1. fetchReviewTravellerThunk.fulfilled - API response received
 * 2. updateTemplateFromStream - Chat stream template received
 * 3. setActiveTemplate - Template set directly (card click)
 *
 * Extracts mutable UI state from immutable raw template data.
 *
 * Note: When agent fills traveller form via chat, backend sends
 * `travellerDetails.isFormValidated: true` to enable "Skip to Payment" button.
 */
export function initializeUiStateFromTemplateData(
  templateData: FlightsReviewTravellersTemplateData,
): ReviewTravellerUiState {
  const { travellerDetails } = templateData;

  // Extract saved travellers from pax groups
  const savedTravellers = extractSavedTravellersFromRawData(travellerDetails);

  // Extract saved contact details
  const savedContactDetails = extractSavedContactDetails(travellerDetails?.contactDetailsForm);

  // Extract saved GST details
  const savedGstDetails = extractSavedGstDetails(travellerDetails?.gstDetailsForm);

  // Initialize fare summary from template data
  const fareSummary: FareSummary = {
    reviewPrice: templateData.reviewPrice ?? null,
    fareBreakup: templateData.fareBreakup ?? null,
  };

  // Check if backend has already validated the traveller form (e.g., agent filled via chat)
  // This enables "Skip to Payment" button when form is pre-filled by agent
  // The flag is inside travellerDetails node
  const isTravellerFormValidated = travellerDetails?.isFormValidated ?? false;

  return {
    savedTravellers,
    savedContactDetails,
    savedGstDetails,
    isTravellerFormValidated,
    fareSummary,
    currentPopup: templateData.currentPopup ?? null,
    isAddonLoading: false,
    // TODO: remove this once we have a proper versioning system
    templateStreamVersion: 0,
    validatedTravellerIds: [],
  };
}

// ============================================
// Saved Travellers Helpers
// ============================================

/**
 * Extract saved travellers from raw template data
 * Transforms API format (ExtendedPaxGroups) to UI state format (SavedTravellersState)
 */
export function extractSavedTravellersFromRawData(
  travellerDetails: FlightsReviewTravellersTemplateData['travellerDetails'],
): SavedTravellersState {
  const savedTravellers: SavedTravellersState = {};

  if (!travellerDetails?.paxForm?.paxGroups) {
    return savedTravellers;
  }

  const { paxGroups } = travellerDetails.paxForm;
  const paxTypes: PaxType[] = ['ADULT', 'CHILD', 'INFANT'];

  for (const paxType of paxTypes) {
    const paxGroup = paxGroups[paxType];
    if (paxGroup?.savedTravellers && paxGroup.savedTravellers.length > 0) {
      savedTravellers[paxType] = paxGroup.savedTravellers.map((traveller) => ({
        id: traveller.travellerId,
        isPreSelected: !!traveller.isSelected,
        isOpenForm: !!traveller.isOpenForm,
        rowFields: { ...traveller.rowFields },
      }));
    }
  }

  return savedTravellers;
}

/**
 * @deprecated Use extractSavedTravellersFromRawData instead
 * Kept for backward compatibility
 */
export function extractSavedTravellersFromPaxForm(
  travellerDetails: FlightsReviewTravellersTemplateData['travellerDetails'],
): SavedTravellersState {
  return extractSavedTravellersFromRawData(travellerDetails);
}

/**
 * Extract saved contact details from API response
 */
export function extractSavedContactDetails(
  contactDetailsForm: ContactDetailsFormConfig | null | undefined,
): ContactDetailsFormValues {
  if (!contactDetailsForm?.savedContactDetails) {
    return EMPTY_CONTACT_DETAILS;
  }

  const saved = contactDetailsForm.savedContactDetails;
  return {
    COUNTRY_CODE: saved.COUNTRY_CODE ?? '',
    COUNTRY_NAME: saved.COUNTRY_NAME ?? '',
    EMAIL: saved.EMAIL ?? '',
    MOBILE_NUMBER: saved.MOBILE_NUMBER ?? '',
  };
}

/**
 * Extract saved GST details from API response
 * Auto-sets optIn to true if company name or registration number exists
 */
export function extractSavedGstDetails(
  gstDetailsForm: GstDetailsFormConfig | null | undefined,
): GstDetailsFormValues {
  if (!gstDetailsForm?.savedGstDetails) {
    return EMPTY_GST_DETAILS;
  }

  const saved = gstDetailsForm.savedGstDetails;
  const hasCompanyName =
    saved.REGISTERED_COMPANY_NAME !== undefined && saved.REGISTERED_COMPANY_NAME !== '';
  const hasRegistrationNo = saved.REGISTRATION_NO !== undefined && saved.REGISTRATION_NO !== '';

  return {
    REGISTERED_COMPANY_NAME: saved.REGISTERED_COMPANY_NAME ?? '',
    REGISTRATION_NO: saved.REGISTRATION_NO ?? '',
    optIn: hasCompanyName || hasRegistrationNo,
  };
}

// ============================================
// Traveller Update Helpers
// ============================================

/**
 * Update a traveller's selection status in the saved travellers state
 */
export function updateTravellerSelection(
  savedTravellers: SavedTravellersState,
  paxType: PaxType,
  travellerId: string,
  isPreSelected: boolean,
): SavedTravellersState {
  const travellers = savedTravellers[paxType];
  if (!travellers) {
    return savedTravellers;
  }

  return {
    ...savedTravellers,
    [paxType]: travellers.map((t) => (t.id === travellerId ? { ...t, isPreSelected } : t)),
  };
}

/**
 * Update a traveller's row fields in the saved travellers state
 */
export function updateTravellerRowFields(
  savedTravellers: SavedTravellersState,
  paxType: PaxType,
  travellerId: string,
  rowFields: Record<string, string>,
): SavedTravellersState {
  const travellers = savedTravellers[paxType];
  if (!travellers) {
    return savedTravellers;
  }

  return {
    ...savedTravellers,
    [paxType]: travellers.map((t) => (t.id === travellerId ? { ...t, rowFields } : t)),
  };
}

// ============================================
// Interstitial State Helpers
// ============================================

/**
 * Interstitial state shape for applying extracted data
 * This interface represents the mutable state in Immer reducers for templateInterstitialSlice
 */
export interface InterstitialStateMutable {
  selectedFares: SelectedFareState;
  selectedFareCombo: SelectedFareCombo;
}

/**
 * Apply interstitial data to Redux state
 *
 * Used by both updateTemplateFromStream and setActiveTemplate extraReducers
 * to eliminate code duplication when handling flights-review-interstitial template.
 *
 * @param state - Mutable Immer state from reducer (templateInterstitialSlice)
 * @param interstitialData - Template data for interstitial
 */
export function applyInterstitialDataToState(
  state: InterstitialStateMutable,
  interstitialData: FlightsReviewInterstitialTemplateData,
): void {
  const { selectedFares, selectedFareCombo } = extractSelectedFaresWithCombo(interstitialData);
  state.selectedFares = selectedFares;
  state.selectedFareCombo = selectedFareCombo;
}
